import { z } from 'zod';
import { newAccessToken, newIdempotencyKey } from '@/lib/ids';
import { readJson, removeKey, writeJson } from '@/lib/safe-storage';

export const contactsSchema = z.object({
  name: z.string().trim().min(2, 'Укажите имя').max(80, 'Слишком длинное имя'),
  phone: z
    .string()
    .trim()
    .refine((v) => {
      const digits = v.replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 15;
    }, 'Номер телефона, например +7 900 123-45-67'),
  car: z.string().trim().min(2, 'Укажите марку и модель').max(80, 'Слишком длинно'),
  plate: z.string().trim().max(16, 'Не больше 16 символов').optional().or(z.literal('')),
  comment: z.string().trim().max(500, 'Не больше 500 символов').optional().or(z.literal('')),
});
export type Contacts = z.infer<typeof contactsSchema>;

export interface BookingDraft {
  contacts: Contacts;
  remember: boolean;
  /** Key and token of the request in flight; reused on retries of the same request. */
  attempt?: { signature: string; idempotencyKey: string; accessToken: string };
}

const key = (slug: string) => `plansgx:${slug}:draft`;

export const emptyContacts: Contacts = { name: '', phone: '', car: '', plate: '', comment: '' };

export function loadDraft(slug: string): BookingDraft | null {
  return readJson<BookingDraft | null>(key(slug), null, 'session');
}

export function saveDraft(slug: string, draft: BookingDraft) {
  writeJson(key(slug), draft, 'session');
}

export function clearDraft(slug: string) {
  removeKey(key(slug), 'session');
}

/**
 * Same request (service, time, phone) → same idempotency key and access token, so a
 * retry after a lost response returns the booking that was already created. A
 * different request gets new ones.
 */
export function attemptFor(draft: BookingDraft, serviceId: string, startsAt: string) {
  const signature = `${serviceId}|${new Date(startsAt).getTime()}|${draft.contacts.phone.replace(/\D/g, '')}`;
  if (draft.attempt?.signature === signature) return draft.attempt;
  return { signature, idempotencyKey: newIdempotencyKey(), accessToken: newAccessToken() };
}
